import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {statuses, useWorkDate, useWorkOrder, useWorkOrderSimilar } from '../../Data/Jobs';
import { usePreference } from '../../Data/Preference';
import { getDateStr } from '../../Components/Formatting/DateTime';
import { hashCode, useRedirect, useRouteLink, useSearchParamsOnce } from '../../Services/helpers';
import { IDs } from '../../Data/Misc';
import { useSyncComplete } from '../../Components/Offline';
import { useSession } from '../../Components/Session';
import MarkAsDone from '../Customer/MarkAsDone';
import Skip from '../Customer/Skip';
import ScheduleJob from '../Customer/ScheduleJob';
import Optimise from './Modals/Optimise';

const WorkViewContext = createContext();

export const useWorkViewContext = () => {
    return useContext(WorkViewContext);
};

export const Provider = ({ children, date, filterOverride }) => {
    const [persistedAssignedTo, setPersistedAssignedTo] = usePreference('work:assignedTo', null);
    const [persistedRoundId, setPersistedRoundId] = usePreference('work:round', null, true);
    const [persistedHideCompleted, setPersistedHideCompleted] = usePreference('work:hideCompleted', false);
    const {jobs, refresh: refreshJobs, offlineUnavailable} = useWorkDate(date, null, true);
    const [markingJobDone, setMarkingJobDone] = useState(null);
    const [skipping, setSkipping] = useState(null);
    const [scheduling, setScheduling] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [isReordering, setIsReordering] = useState(false);
    const [preferencesOpen, setPreferencesOpen] = useState(false);
    const [isOrderInvalid, setIsOrderInvalid] = useState(false);
    const [optimiseOpen, setOptimiseOpen] = useState(false);
    const {go, Redirect} = useRedirect();
    const {user} = useSession();
    const routeLink = useRouteLink();
    const searchData = useSearchParamsOnce();

    useEffect(() => {
        if (searchData?.assignedTo) {
            setPersistedAssignedTo(searchData.assignedTo);
        }
        if (searchData?.roundId) {
            setPersistedRoundId(searchData.roundId);
        }
        if (searchData?.hideCompleted) {
            setPersistedHideCompleted(searchData.hideCompleted);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchData]);

    const assignedTo = useMemo(() => {
        return filterOverride?.assignedTo !== undefined ? filterOverride.assignedTo : persistedAssignedTo;
    }, [filterOverride?.assignedTo, persistedAssignedTo]);

    const roundId = useMemo(() => {
        return filterOverride?.roundId !== undefined ? filterOverride.roundId : persistedRoundId;
    }, [filterOverride?.roundId, persistedRoundId]);

    const hideCompleted = useMemo(() => {
        return filterOverride?.hideCompleted !== undefined ? filterOverride.hideCompleted : persistedHideCompleted;
    }, [filterOverride?.hideCompleted, persistedHideCompleted]);

    const {order, reorder, optimise, refresh: refreshOrder, isLoading: workOrderLoading, deleteWorkOrder} = useWorkOrder({date, assignedTo, roundId}, true);

    const dateStr = useMemo(() => getDateStr(date, false, true), [date]);

    const expandNext = () => {
        const open = expanded ?? markingJobDone?.id ?? skipping?.id ?? scheduling?.id ?? null;

        if (!filteredJobs || !open) {
            return;
        }

        const next = findNextJob(filteredJobs, open, order);
        setExpanded(next ?? null);
    }

    const refreshAndExpandNext = () => {
        expandNext();
        refreshJobs();
    }

    const goBack = () => {
        const d = new Date(date+'T12:12:12Z');
        d.setDate(d.getDate() - 1);
        go('/work/'+d.toISOString().split('T')[0]);
    }

    const goForward = () => {
        const d = new Date(date+'T12:12:12Z');
        d.setDate(d.getDate() + 1);
        go('/work/'+d.toISOString().split('T')[0]);
    }

    useEffect(() => {
        refreshOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignedTo, roundId, date]);

    useSyncComplete(() => {
        refreshJobs();
    });

    const roundIds = useMemo(() => {
        return jobs?.map(j => j.roundId).filter(id => id) ?? [];
    }, [jobs]);

    const filteredJobs = useMemo(() => {
        if (!jobs) {
            return [];
        }

        let tmpJobs = jobs;

        if (order && order.jobIds && order.jobIds.length > 0) {
            const orderedJobs = order.jobIds.map(id => jobs.find(j => j.id === id)).filter(j => j);
            const unorderedJobs = jobs.filter(j => !order.jobIds.includes(j.id));
            tmpJobs = [...orderedJobs, ...unorderedJobs];
        }

        const filtered = tmpJobs.filter(j => {
            if (assignedTo === null) {
                return true;
            }

            if (assignedTo === IDs.EMPTY) {
                return j.assignedTo === null;
            }

            if (assignedTo === IDs.ONE) {
                return j.assignedTo === null || j.assignedTo === user.id;
            }

            return j.assignedTo === assignedTo;
        })
        .filter(j => {
            if (roundId === null) {
                return true;
            }

            if (roundId === IDs.EMPTY) {
                return j.roundId === null;
            }

            return j.roundId === roundId;
        });

        return filtered;

    }, [jobs, assignedTo, roundId, order, user?.id]);

    const filteredWithoutCompleted = useMemo(() => {
        return filteredJobs.filter(j => {
            if (!hideCompleted) {
                return true;
            }
            return !statuses[j.status].considerDone || j.id === expanded;
        })
    }, [filteredJobs, hideCompleted, expanded]);

    useEffect(() => {
        if (!order || !filteredJobs || !order.jobIds || !filteredJobs.length) {
            setIsOrderInvalid(false);
            return;
        }

        const jobOrderIds = [...order.jobIds].sort();
        const jobIds = [...filteredJobs.map(j => j.id)].sort();

        if (JSON.stringify(jobOrderIds) !== JSON.stringify(jobIds)) {
            setIsOrderInvalid(hashCode(JSON.stringify(jobIds)));
        } else {
            setIsOrderInvalid(false);
        }
    }, [order, filteredJobs]);

    const dateOptions = useMemo(() => {
        return [-2, -1, 0, 1, 2, 3, 4].map(i => {
            const d = new Date(date+'T12:12:12Z');
            d.setDate(d.getDate() + i);
            return {
                icon: i < 0 ? 'UpChevron' : i > 0 ? 'DownChevron' : 'Circle',
                title: getDateStr(d.toISOString().split('T')[0], false, true),
                onClick: () => go('/work/'+d.toISOString().split('T')[0]),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    // Getting a similar work order must happen after the filtered jobs are set

    const {workOrder: similarWorkOrder, refresh: refreshSimilarWorkOrder} = useWorkOrderSimilar(
        filteredJobs?.map(j => j.id) ?? [],
        !!order?.id || workOrderLoading
    );

    const applySimilarWorkOrder = () => {
        const matches = similarWorkOrder.jobIds.filter(id => filteredJobs.some(j => j.id === id));
        const missing = filteredJobs.filter(j => !similarWorkOrder.jobIds.includes(j.id)).map(j => j.id);
        const all = [...matches, ...missing];

        return reorder(all);
    }

    const handleOptimise = async (optimiseOptions) => {
        const jobIds = filteredJobs.map(j => j.id);

        return optimise(jobIds, optimiseOptions);
    }

    useEffect(() => {
        refreshSimilarWorkOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredJobs, order?.id]);

    const providerValues = {
        jobs,
        filteredJobs,
        filteredWithoutCompleted,
        date,
        dateStr,
        dateOptions,
        markingJobDone,
        setMarkingJobDone,
        skipping,
        setSkipping,
        scheduling,
        setScheduling,
        expanded,
        setExpanded,
        calendarOpen,
        setCalendarOpen,
        isReordering,
        setIsReordering,
        preferencesOpen,
        setPreferencesOpen,
        order,
        optimise: () => setOptimiseOpen(true),
        handleOptimise,
        saveOrder: reorder,
        workOrderLoading,
        deleteWorkOrder,
        refreshJobs,
        refreshOrder,
        refreshAndExpandNext,
        goBack,
        goForward,
        assignedTo,
        setAssignedTo: setPersistedAssignedTo,
        roundId,
        setRoundId: setPersistedRoundId,
        roundIds,
        hideCompleted,
        setHideCompleted: setPersistedHideCompleted,
        similarWorkOrder: (similarWorkOrder && similarWorkOrder?.length !== 0) ? similarWorkOrder : null,
        refreshSimilarWorkOrder,
        applySimilarWorkOrder,
        isOrderInvalid,
        offlineUnavailable,
        openRouteLink: () => routeLink(filteredJobs),
    };

    return (
        <WorkViewContext.Provider value={providerValues}>
            {children}

            <MarkAsDone job={markingJobDone}
                onClose={() => setMarkingJobDone(null)}
                then={refreshAndExpandNext}
                actionSource="Work"
            />
            <Skip job={skipping}
                onClose={() => setSkipping(null)}
                then={refreshAndExpandNext}
            />
            <ScheduleJob job={scheduling}
                onClose={() => setScheduling(null)}
                then={refreshAndExpandNext}
            />
            <Optimise
                isOpen={optimiseOpen}
                setIsOpen={setOptimiseOpen}
            />
            <Redirect />
        </WorkViewContext.Provider>
    );
};

const findNextJob = (filteredJobs, jobId) => {
    const index = filteredJobs.findIndex(j => j.id === jobId);

    if (index === -1) {
        return null;
    }

    const next = filteredJobs[index + 1] ?? null;

    return next ? next.id : null;
}
